import PropTypes from 'prop-types';
import { bandOf } from './microSimBands';

/**
 * Participants × skills, coloured by band, with a room-average row. Staff only.
 * Rows are clickable when `onSelect` is given (open that person's report / overlay).
 */
const Cell = ({ score, className = '' }) => {
  const b = bandOf(score);
  return (
    <td className={`p-0.5 ${className}`}>
      <div className={`rounded-md px-1.5 py-1.5 text-center text-xs font-bold tabular-nums ${b.soft} ${b.text}`}>
        {score === null || score === undefined ? '–' : score}
      </div>
    </td>
  );
};
Cell.propTypes = { score: PropTypes.number, className: PropTypes.string };

const GroupHeatmap = ({ skills, people, roomAverage, onSelect, selectedKeys = [] }) => {
  if (!people.length) {
    return <p className="rounded-xl bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">No pair has finished yet. Scores appear here as pairs complete.</p>;
  }
  const keyOf = (p) => `${p.runId}-${p.slot}`;
  return (
    <div className="-mx-1 overflow-x-auto">
      <table className="w-full min-w-[560px] border-separate border-spacing-0 text-left">
        <thead>
          <tr>
            <th className="sticky left-0 bg-white px-2 pb-2 text-xs font-semibold text-neutral-500">Participant</th>
            {skills.map((s) => (
              <th key={s.key} className="px-1 pb-2 text-center text-[11px] font-semibold leading-tight text-neutral-500">{s.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {people.map((p) => {
            const selected = selectedKeys.includes(keyOf(p));
            return (
              <tr
                key={keyOf(p)}
                onClick={onSelect ? () => onSelect(p) : undefined}
                className={onSelect ? `cursor-pointer ${selected ? 'bg-primary-50' : 'hover:bg-neutral-50'}` : ''}
              >
                <td className="sticky left-0 max-w-[160px] truncate bg-inherit px-2 py-1 text-sm font-medium text-neutral-800" title={p.name}>
                  {p.name}
                </td>
                {skills.map((s) => <Cell key={s.key} score={p.scores[s.key]} />)}
              </tr>
            );
          })}
          <tr>
            <td className="sticky left-0 border-t-2 border-neutral-200 bg-white px-2 py-1.5 text-sm font-bold text-neutral-900">Room average</td>
            {skills.map((s) => <Cell key={s.key} score={roomAverage[s.key]} className="border-t-2 border-neutral-200" />)}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

GroupHeatmap.propTypes = {
  skills: PropTypes.array.isRequired,
  people: PropTypes.array.isRequired,
  roomAverage: PropTypes.object.isRequired,
  onSelect: PropTypes.func,
  selectedKeys: PropTypes.arrayOf(PropTypes.string),
};

export default GroupHeatmap;
